// ============================================================
// /jd-enterprises — J&D Enterprises LLC · Martinsburg, WV
// ------------------------------------------------------------
// The Small Business Anchor — Mom-owned utility node for
// families and businesses that need someone reliable in their
// corner. Custom standalone page (not the shared chassis)
// because the pricing needed a single $997 Dominance Line and
// / 4-lane service split don't map cleanly onto the Founder
// Node chassis.
//
// Aesthetic: High-Trust Professional · Deep Navy (#1B2A4A) +
// Crisp White (#FFFFFF) + Warm Gold (#C9A84C). Grit & Gear
// human warmth — organized-desk imagery, community anchor.
// ============================================================
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  ShieldCheck,
  Calendar as CalIcon,
  FolderOpen,
  FileText,
  Receipt,
  Video as VideoIcon,
  Check,
  Trophy,
  ArrowRight,
  ArrowLeft,
  Users,
  Home as HomeIcon,
  Sparkles,
  MapPin,
  Play,
  X as XIcon,
  Compass,
} from "lucide-react";
import { VIDEOS } from "@/config/AssetSpine";

// --- Palette (High-Trust Professional) ------------------------
const P = {
  NAVY:     "#1B2A4A",
  NAVY_HI:  "#243456",
  NAVY_DEEP:"#0F1B33",
  WHITE:    "#FFFFFF",
  CREAM:    "#F5F1E8",
  GOLD:     "#C9A84C",
  GOLD_HI:  "#DEBE68",
  GOLD_DEEP:"#8F771D",
  MUTED:    "#B6C0D2",
  HAIRLINE: "#2B3960",
};

const PHONE = "440-771-8409";

// --- Intake categories ---------------------------------------
const INTAKE_CATEGORIES = [
  {
    id: "notary",
    label: "Notary Bookings",
    blurb: "Certified mobile notary — signatures witnessed at your home, office, or ours.",
    icon: FileText,
  },
  {
    id: "tax",
    label: "Tax Prep Inquiries",
    blurb: "Personal + small-business filings, quarterly estimates, gentle year-round guidance.",
    icon: Receipt,
  },
  {
    id: "general",
    label: "General Local Services",
    blurb: "Community-first admin help — errands the day gets away from you handling.",
    icon: Users,
  },
];

// --- Command Calendar lanes ----------------------------------
const SERVICE_LANES = [
  { id: "notary",   label: "Notary",        color: P.GOLD,   subLabel: "Mobile · in-office" },
  { id: "booking",  label: "Bookkeeping",   color: "#7CC4DA",subLabel: "Reconciliation · books" },
  { id: "tax",      label: "Tax Prep",      color: "#E5B14F",subLabel: "Personal · small biz" },
  { id: "admin",    label: "Admin Support", color: "#A78BFA",subLabel: "Files · errands" },
];

const CAL_DAYS = [
  { key: "mon", label: "Mon", date: "Feb 03" },
  { key: "tue", label: "Tue", date: "Feb 04" },
  { key: "wed", label: "Wed", date: "Feb 05" },
  { key: "thu", label: "Thu", date: "Feb 06" },
  { key: "fri", label: "Fri", date: "Feb 07" },
];

const CAL_GRID = [
  { day: "mon", time: "9:00 AM",  lane: "notary",  client: "Higgins Estate Signing" },
  { day: "mon", time: "1:30 PM",  lane: "booking", client: "Blue Ridge Bakery · Q1 Books" },
  { day: "tue", time: "10:00 AM", lane: "tax",     client: "The Freemans · Personal 1040" },
  { day: "tue", time: "3:00 PM",  lane: "admin",   client: "Community Center · File Sort" },
  { day: "wed", time: "9:00 AM",  lane: "notary",  client: "Martinsburg Realty · 4 Docs" },
  { day: "wed", time: "2:00 PM",  lane: "booking", client: "Lena's Landscaping · Payroll" },
  { day: "thu", time: "11:00 AM", lane: "tax",     client: "Delaney LLC · Quarterly Est." },
  { day: "thu", time: "4:00 PM",  lane: "admin",   client: "Rev. Coles · Church Filings" },
  { day: "fri", time: "10:00 AM", lane: "notary",  client: "VA Hospital · Power of Attorney" },
  { day: "fri", time: "2:30 PM",  lane: "booking", client: "Ridge Auto · Monthly Close" },
];

// --- Document Vault ------------------------------------------
const VAULT_FOLDERS = [
  { name: "Freeman Family · Tax 2025",    files: 12, tag: "Personal" },
  { name: "Blue Ridge Bakery · Books",     files: 47, tag: "Small Biz" },
  { name: "Higgins Estate · Notary Log",   files:  8, tag: "Notary" },
  { name: "Community Ctr · Admin Files",   files: 23, tag: "Community" },
];

const INTERACTION_TIMELINE = [
  { when: "Today · 9:12 AM", who: "The Freemans", event: "Signed 1040 draft returned — final review Wed."},
  { when: "Yesterday",        who: "Blue Ridge Bakery", event: "Q1 books reconciled · 2 flags cleared." },
  { when: "Feb 01",           who: "Higgins Estate", event: "3 documents notarized · originals filed." },
  { when: "Jan 28",           who: "Community Center", event: "Grant paperwork sorted + indexed." },
];

// --- Billing sample ------------------------------------------
const INVOICES = [
  { id: "INV-2402-001", client: "Blue Ridge Bakery",    plan: "Monthly Bookkeeping",    amount: 385, status: "Paid",     due: "Feb 01" },
  { id: "INV-2402-002", client: "The Freemans",          plan: "Tax Prep · Personal",    amount: 425, status: "Pending",  due: "Feb 10" },
  { id: "INV-2402-003", client: "Lena's Landscaping",    plan: "Quarterly Payroll",      amount: 640, status: "Paid",     due: "Feb 01" },
  { id: "INV-2402-004", client: "Rev. Coles Church",     plan: "Admin Support · Monthly",amount: 275, status: "Overdue",  due: "Jan 25" },
];

// --- Growth Engine content -----------------------------------
const GROWTH_POSTS = [
  { day: "Mon", type: "Community Tips",    title: "3 Documents Every WV Homeowner Should Notarize" },
  { day: "Mon", type: "Organizational",    title: "Kitchen-Table Filing System · 60-Second Setup" },
  { day: "Tue", type: "Community Tips",    title: "How to Read Your Berkeley County Tax Notice" },
  { day: "Wed", type: "Organizational",    title: "Small-Biz Receipt Sort · One Envelope Per Month" },
  { day: "Wed", type: "Community Tips",    title: "Free Notary Days · Where + When in Martinsburg" },
  { day: "Thu", type: "Organizational",    title: "Digitizing Old Paper Records Safely" },
  { day: "Fri", type: "Community Tips",    title: "First-Time Home Buyer · Doc Checklist" },
  { day: "Fri", type: "Organizational",    title: "Year-End Prep Starts in July — Here's How" },
  { day: "Sat", type: "Community Tips",    title: "Grant Paperwork for Small Churches + Nonprofits" },
  { day: "Sun", type: "Organizational",    title: "The 15-Minute Weekly Financial Reset" },
];

// --- Pricing (single tier · Dominance Line) -----------------
const DOMINANCE_TIER = {
  id: "dominance",
  label: "Dominance Line",
  price: "$997",
  period: "/month",
  tagline: "The Full System — no compromises, no comparisons.",
  features: [
    "Full System Shield — complete operational coverage",
    "2 Strategy Sprints per month with Amanda + the Grid",
    "10 Cinematic Videos per week — full content engine",
    "Unlimited notary + priority scheduling",
    "Full bookkeeping — up to 3 entities",
    "Tax prep · personal + small-biz filings",
    "Same-day admin support · dedicated line",
    "Unlimited document vault + off-site backup",
    "Community anchor listing on the Grid",
  ],
};

// ============================================================
// Root
// ============================================================
export default function JDEnterprises() {
  return (
    <div
      className="min-h-screen text-white relative"
      style={{ background: P.NAVY }}
      data-testid="jd-enterprises-root"
    >
      <Backdrop />
      <TopBar />
      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12 space-y-12 sm:space-y-16">
        <ModuleGreeting />
        <ModuleIntakeShield />
        <ModuleCommandCalendar />
        <ModuleUtilitySpine />
        <ModuleBillingAutopilot />
        <ModuleGrowthEngine />
        <PricingSection />
        <Footer />
      </main>
      <TitanTour />
    </div>
  );
}

// ============================================================
// Backdrop — subtle architectural office feel via layered
// gradients (no external image needed).
// ============================================================
function Backdrop() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(1200px 600px at 15% -10%, ${P.GOLD}0F 0%, transparent 60%),
            radial-gradient(900px 500px at 100% 20%, ${P.NAVY_HI}CC 0%, transparent 70%),
            linear-gradient(180deg, ${P.NAVY} 0%, ${P.NAVY_DEEP} 100%)
          `,
        }}
      />
      {/* faint grid lines for the "organized office" feel */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: `
            linear-gradient(${P.GOLD}44 1px, transparent 1px),
            linear-gradient(90deg, ${P.GOLD}44 1px, transparent 1px)
          `,
          backgroundSize: "72px 72px",
        }}
      />
    </div>
  );
}

// ============================================================
// Top bar
// ============================================================
function TopBar() {
  return (
    <header
      className="relative z-10 border-b"
      style={{
        borderColor: P.HAIRLINE,
        background: `linear-gradient(180deg, ${P.GOLD}0F 0%, ${P.NAVY}F0 100%)`,
        backdropFilter: "blur(6px)",
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded flex items-center justify-center shrink-0"
            style={{ background: P.GOLD, color: P.NAVY, fontWeight: 700 }}
          >
            J&D
          </div>
          <div className="min-w-0">
            <div
              className="text-[10px] uppercase font-mono"
              style={{ color: P.GOLD, letterSpacing: "0.32em" }}
            >
              J&D Enterprises · LLC
            </div>
            <div className="text-sm text-white flex items-center gap-1.5">
              <MapPin size={11} style={{ color: P.GOLD }} />
              Martinsburg, WV
            </div>
          </div>
        </div>
        <div
          className="hidden sm:flex items-center gap-2 text-[10px] font-mono uppercase"
          style={{ color: P.MUTED, letterSpacing: "0.28em" }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: P.GOLD, boxShadow: `0 0 12px ${P.GOLD}` }}
          />
          The Small Business Anchor
        </div>
      </div>
    </header>
  );
}

// ============================================================
// Module 01 · Greeting
// ============================================================
function ModuleGreeting() {
  return (
    <section id="greeting" data-testid="jde-module-greeting" className="scroll-mt-24">
      <ModuleKicker index="01" title="Greeting" />
      <div
        className="rounded-lg border overflow-hidden"
        style={{
          borderColor: `${P.GOLD}44`,
          background: `linear-gradient(180deg, ${P.NAVY_HI} 0%, ${P.NAVY} 100%)`,
          boxShadow: `0 30px 80px -30px ${P.NAVY_DEEP}, inset 0 1px 0 ${P.GOLD}22`,
        }}
      >
        <div className="grid md:grid-cols-2 gap-0">
          {/* Amanda video */}
          <div className="relative bg-black min-h-[240px]">
            <video
              src={VIDEOS.AMANDA_FOUNDER}
              controls
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
              style={{ aspectRatio: "16 / 9", display: "block" }}
              data-testid="jde-greeting-video"
            />
            <div
              className="absolute top-3 left-3 px-2.5 py-1 rounded text-[10px] font-mono uppercase"
              style={{
                background: `${P.NAVY}CC`,
                color: P.GOLD,
                letterSpacing: "0.24em",
                border: `1px solid ${P.GOLD}66`,
              }}
            >
              Amanda 4.0 · Live
            </div>
          </div>
          {/* Message */}
          <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
            <div
              className="text-[10px] font-mono uppercase mb-3"
              style={{ color: P.GOLD, letterSpacing: "0.32em" }}
            >
              Welcome · Community Anchor
            </div>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-4"
              style={{ color: P.WHITE }}
              data-testid="jde-hero-title"
            >
              Welcome to <span style={{ color: P.GOLD }}>J&D Enterprises LLC</span>
            </h1>
            <p
              className="text-base sm:text-lg leading-relaxed"
              style={{ color: P.MUTED }}
              data-testid="jde-hero-subtitle"
            >
              <span style={{ color: P.WHITE, fontWeight: 500 }}>The Small Business Anchor.</span>{" "}
              Mom-owned, helping families and businesses stay organized — one signed
              document, one clean set of books, one calm afternoon at a time.
            </p>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <Pill icon={ShieldCheck} label="Certified Notary · WV" />
              <Pill icon={Users} label="Family + Small Business" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Module 02 · Intake Shield
// ============================================================
function ModuleIntakeShield() {
  const [chosen, setChosen] = useState("notary");
  return (
    <section id="intake" data-testid="jde-module-intake" className="scroll-mt-24">
      <ModuleKicker index="02" title="Intake Shield" />
      <div
        className="rounded-lg border overflow-hidden"
        style={{
          borderColor: `${P.GOLD}44`,
          background: P.NAVY_HI,
          boxShadow: `inset 0 1px 0 ${P.GOLD}22`,
        }}
      >
        <div className="p-6 sm:p-8">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6 lg:gap-10">
            {/* Phone + CTA */}
            <div>
              <div
                className="text-[10px] font-mono uppercase mb-2"
                style={{ color: P.GOLD, letterSpacing: "0.28em" }}
              >
                Call the Anchor
              </div>
              <a
                href={`tel:${PHONE.replace(/-/g, "")}`}
                className="inline-flex items-center gap-3 text-3xl sm:text-4xl font-semibold"
                style={{ color: P.WHITE, textDecoration: "none" }}
                data-testid="jde-intake-phone"
              >
                <Phone size={26} style={{ color: P.GOLD }} />
                {PHONE}
              </a>
              <p className="mt-3 text-sm sm:text-base" style={{ color: P.MUTED }}>
                Amanda picks up every call live. Choose the category below and she routes
                you to the right lane the moment you connect.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href={`tel:${PHONE.replace(/-/g, "")}`}
                  data-testid="jde-intake-call-cta"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded font-semibold text-sm uppercase"
                  style={{
                    background: P.GOLD,
                    color: P.NAVY,
                    letterSpacing: "0.16em",
                    boxShadow: `0 8px 22px -8px ${P.GOLD}`,
                    textDecoration: "none",
                  }}
                >
                  <Phone size={14} /> Book Service
                </a>
                <button
                  type="button"
                  data-testid="jde-intake-callback-cta"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded font-semibold text-sm uppercase"
                  style={{
                    background: "transparent",
                    color: P.GOLD,
                    border: `1px solid ${P.GOLD}88`,
                    letterSpacing: "0.16em",
                  }}
                >
                  Call Me Later
                </button>
              </div>
            </div>
            {/* Yesterday stats */}
            <div
              className="rounded p-5 border h-fit"
              style={{ borderColor: P.HAIRLINE, background: P.NAVY_DEEP }}
            >
              <div
                className="text-[10px] font-mono uppercase mb-3"
                style={{ color: P.GOLD, letterSpacing: "0.28em" }}
              >
                Yesterday · Amanda&apos;s Watch
              </div>
              <div className="grid grid-cols-3 gap-3">
                <StatCell k="Calls captured" v="14" />
                <StatCell k="Bookings won"   v="9"  />
                <StatCell k="Lost leads"     v="0"  />
              </div>
            </div>
          </div>

          {/* Three intake tiles */}
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {INTAKE_CATEGORIES.map((c) => {
              const Icon = c.icon;
              const active = c.id === chosen;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setChosen(c.id)}
                  data-testid={`jde-intake-tile-${c.id}`}
                  className="text-left rounded-lg p-5 transition-all"
                  style={{
                    border: `1px solid ${active ? P.GOLD : P.HAIRLINE}`,
                    background: active
                      ? `linear-gradient(160deg, ${P.GOLD}22 0%, ${P.NAVY_DEEP} 100%)`
                      : P.NAVY_DEEP,
                    boxShadow: active ? `0 12px 30px -12px ${P.GOLD}88` : "none",
                    cursor: "pointer",
                  }}
                >
                  <div
                    className="w-10 h-10 rounded flex items-center justify-center mb-3"
                    style={{ background: `${P.GOLD}18`, color: P.GOLD, border: `1px solid ${P.GOLD}44` }}
                  >
                    <Icon size={18} />
                  </div>
                  <div className="text-base font-semibold mb-1.5" style={{ color: P.WHITE }}>
                    {c.label}
                  </div>
                  <div className="text-sm leading-relaxed" style={{ color: P.MUTED }}>
                    {c.blurb}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Module 03 · Command Calendar
// ============================================================
function ModuleCommandCalendar() {
  return (
    <section id="calendar" data-testid="jde-module-calendar" className="scroll-mt-24">
      <ModuleKicker index="03" title="Command Calendar · Multi-Service Dispatch" />
      <div
        className="rounded-lg border overflow-hidden"
        style={{
          borderColor: `${P.GOLD}44`,
          background: P.NAVY_HI,
        }}
      >
        <div className="p-6 sm:p-8">
          {/* Lane legend */}
          <div className="flex flex-wrap gap-3 mb-6">
            {SERVICE_LANES.map((l) => (
              <div
                key={l.id}
                data-testid={`jde-lane-${l.id}`}
                className="rounded px-3 py-2 flex items-center gap-2 border"
                style={{ borderColor: `${l.color}88`, background: `${l.color}14` }}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: l.color, boxShadow: `0 0 10px ${l.color}` }}
                />
                <div>
                  <div className="text-sm font-semibold" style={{ color: P.WHITE }}>{l.label}</div>
                  <div
                    className="text-[10px] font-mono uppercase"
                    style={{ color: P.MUTED, letterSpacing: "0.22em" }}
                  >
                    {l.subLabel}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Grid */}
          <div
            className="overflow-x-auto rounded"
            style={{ border: `1px solid ${P.HAIRLINE}`, background: P.NAVY_DEEP }}
          >
            <table className="w-full" style={{ borderCollapse: "collapse", minWidth: 720 }} data-testid="jde-calendar-grid">
              <thead>
                <tr>
                  <th className="p-3 text-left text-[10px] font-mono uppercase" style={{ color: P.GOLD, letterSpacing: "0.24em", borderBottom: `1px solid ${P.HAIRLINE}` }}>
                    When
                  </th>
                  <th className="p-3 text-left text-[10px] font-mono uppercase" style={{ color: P.GOLD, letterSpacing: "0.24em", borderBottom: `1px solid ${P.HAIRLINE}` }}>
                    Lane
                  </th>
                  <th className="p-3 text-left text-[10px] font-mono uppercase" style={{ color: P.GOLD, letterSpacing: "0.24em", borderBottom: `1px solid ${P.HAIRLINE}` }}>
                    Client / Service
                  </th>
                </tr>
              </thead>
              <tbody>
                {CAL_GRID.map((row, i) => {
                  const lane = SERVICE_LANES.find((l) => l.id === row.lane);
                  const dayLabel = CAL_DAYS.find((d) => d.key === row.day);
                  return (
                    <tr key={i} style={{ borderTop: `1px solid ${P.HAIRLINE}` }}>
                      <td className="p-3">
                        <div className="text-sm font-semibold" style={{ color: P.WHITE }}>
                          {dayLabel?.label} · {row.time}
                        </div>
                        <div className="text-[10px] font-mono" style={{ color: P.MUTED, letterSpacing: "0.2em" }}>
                          {dayLabel?.date}
                        </div>
                      </td>
                      <td className="p-3">
                        <span
                          className="inline-block px-2 py-1 rounded text-[10px] font-mono uppercase"
                          style={{
                            background: `${lane.color}18`,
                            color: lane.color,
                            border: `1px solid ${lane.color}55`,
                            letterSpacing: "0.2em",
                          }}
                        >
                          {lane.label}
                        </span>
                      </td>
                      <td className="p-3 text-sm" style={{ color: P.WHITE }}>{row.client}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[10px] font-mono uppercase" style={{ color: P.MUTED, letterSpacing: "0.22em" }}>
            10 dispatches on the board · this week
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Module 04 · Utility Spine
// ============================================================
function ModuleUtilitySpine() {
  return (
    <section id="spine" data-testid="jde-module-spine" className="scroll-mt-24">
      <ModuleKicker index="04" title="Utility Spine · Document Vault + Interaction History" />
      <div className="grid md:grid-cols-2 gap-6">
        {/* Document Vault */}
        <div
          className="rounded-lg border p-6"
          style={{
            borderColor: `${P.GOLD}44`,
            background: P.NAVY_HI,
          }}
          data-testid="jde-vault"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-semibold" style={{ color: P.WHITE }}>Your Documents</div>
            <button
              type="button"
              data-testid="jde-vault-upload"
              className="text-[10px] font-mono uppercase px-3 py-1.5 rounded"
              style={{
                border: `1px solid ${P.GOLD}88`,
                color: P.GOLD,
                background: `${P.GOLD}14`,
                letterSpacing: "0.24em",
              }}
            >
              + Upload
            </button>
          </div>
          <div className="space-y-2">
            {VAULT_FOLDERS.map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded"
                style={{ background: P.NAVY_DEEP, border: `1px solid ${P.HAIRLINE}` }}
              >
                <FolderOpen size={18} style={{ color: P.GOLD }} className="shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate" style={{ color: P.WHITE }}>{f.name}</div>
                  <div className="text-[10px] font-mono uppercase" style={{ color: P.MUTED, letterSpacing: "0.22em" }}>
                    {f.files} files · {f.tag}
                  </div>
                </div>
                <ShieldCheck size={14} style={{ color: P.GOLD }} />
              </div>
            ))}
          </div>
          <div
            className="mt-4 rounded border-2 border-dashed p-4 text-center text-sm"
            style={{ borderColor: `${P.GOLD}55`, color: P.MUTED }}
          >
            Drop files here — encrypted at rest, backed up nightly.
          </div>
        </div>

        {/* Interaction History */}
        <div
          className="rounded-lg border p-6"
          style={{
            borderColor: `${P.GOLD}44`,
            background: P.NAVY_HI,
          }}
          data-testid="jde-timeline"
        >
          <div className="text-sm font-semibold mb-4" style={{ color: P.WHITE }}>
            Interaction Timeline
          </div>
          <div className="relative pl-5">
            <div
              className="absolute top-1 bottom-1 left-1 w-px"
              style={{ background: `${P.GOLD}55` }}
            />
            <div className="space-y-4">
              {INTERACTION_TIMELINE.map((t, i) => (
                <div key={i} className="relative">
                  <div
                    className="absolute -left-[16px] top-1 w-2.5 h-2.5 rounded-full"
                    style={{ background: P.GOLD, boxShadow: `0 0 12px ${P.GOLD}` }}
                  />
                  <div
                    className="text-[10px] font-mono uppercase"
                    style={{ color: P.GOLD, letterSpacing: "0.24em" }}
                  >
                    {t.when}
                  </div>
                  <div className="text-sm font-medium mt-0.5" style={{ color: P.WHITE }}>{t.who}</div>
                  <div className="text-sm mt-0.5" style={{ color: P.MUTED }}>{t.event}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Module 05 · Billing Autopilot
// ============================================================
function ModuleBillingAutopilot() {
  const statusStyle = (s) => {
    if (s === "Paid")    return { color: "#7CE1A8", bg: "#0F3D1E", border: "#5CB85C" };
    if (s === "Pending") return { color: P.GOLD,    bg: `${P.GOLD}18`, border: `${P.GOLD}88` };
    return                    { color: "#F5A2A2", bg: "#3a1010",  border: "#B85C5C" };
  };
  return (
    <section id="billing" data-testid="jde-module-billing" className="scroll-mt-24">
      <ModuleKicker index="05" title="Billing Autopilot · Recurring Admin Services" />
      <div
        className="rounded-lg border overflow-hidden"
        style={{ borderColor: `${P.GOLD}44`, background: P.NAVY_HI }}
      >
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
            <div>
              <div className="text-sm font-semibold" style={{ color: P.WHITE }}>Invoices · This Cycle</div>
              <div className="text-[11px] font-mono" style={{ color: P.MUTED, letterSpacing: "0.22em" }}>
                Monthly + quarterly billing · autopay ready
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <BillingChip label="Monthly" active />
              <BillingChip label="Quarterly" />
              <BillingChip label="Annual" />
            </div>
          </div>

          <div
            className="rounded overflow-hidden"
            style={{ border: `1px solid ${P.HAIRLINE}` }}
            data-testid="jde-invoices-table"
          >
            <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
              <thead style={{ background: P.NAVY_DEEP }}>
                <tr>
                  <TH>Invoice</TH>
                  <TH>Client</TH>
                  <TH>Plan</TH>
                  <TH align="right">Amount</TH>
                  <TH>Due</TH>
                  <TH>Status</TH>
                </tr>
              </thead>
              <tbody>
                {INVOICES.map((inv) => {
                  const ss = statusStyle(inv.status);
                  return (
                    <tr key={inv.id} style={{ borderTop: `1px solid ${P.HAIRLINE}` }}>
                      <TD>
                        <span className="font-mono text-[11px]" style={{ color: P.GOLD, letterSpacing: "0.18em" }}>
                          {inv.id}
                        </span>
                      </TD>
                      <TD><span style={{ color: P.WHITE }}>{inv.client}</span></TD>
                      <TD><span style={{ color: P.MUTED }}>{inv.plan}</span></TD>
                      <TD align="right">
                        <span style={{ color: P.WHITE, fontWeight: 600 }}>${inv.amount}</span>
                      </TD>
                      <TD><span style={{ color: P.MUTED }}>{inv.due}</span></TD>
                      <TD>
                        <span
                          className="inline-block px-2 py-1 rounded text-[10px] font-mono uppercase"
                          style={{
                            color: ss.color,
                            background: ss.bg,
                            border: `1px solid ${ss.border}`,
                            letterSpacing: "0.2em",
                          }}
                        >
                          {inv.status}
                        </span>
                      </TD>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Module 06 · Growth Engine
// ============================================================
function ModuleGrowthEngine() {
  return (
    <section id="growth" data-testid="jde-module-growth" className="scroll-mt-24">
      <ModuleKicker index="06" title="Growth Engine · Cinematic Content" />
      <div
        className="rounded-lg border overflow-hidden"
        style={{
          borderColor: `${P.GOLD}44`,
          background: P.NAVY_HI,
        }}
      >
        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold" style={{ color: P.WHITE }}>
                10 Cinematic Videos every week
              </h3>
              <p className="mt-2 text-sm" style={{ color: P.MUTED }}>
                Community tips + organizational hacks — Amanda drafts them, we film them, the
                neighborhood watches.
              </p>
            </div>
            <div
              className="rounded px-3 py-2 text-[10px] font-mono uppercase"
              style={{
                background: `${P.GOLD}18`,
                color: P.GOLD,
                border: `1px solid ${P.GOLD}66`,
                letterSpacing: "0.24em",
              }}
              data-testid="jde-growth-pill"
            >
              10 Cinematic Videos · Weekly Cadence
            </div>
          </div>

          {/* Content grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {GROWTH_POSTS.map((p, i) => (
              <div
                key={i}
                className="rounded border overflow-hidden"
                style={{ borderColor: P.HAIRLINE, background: P.NAVY_DEEP }}
                data-testid={`jde-growth-post-${i}`}
              >
                <div
                  className="aspect-video flex items-center justify-center relative"
                  style={{
                    background: `linear-gradient(135deg, ${P.NAVY} 0%, ${P.NAVY_HI} 60%, ${P.GOLD}22 100%)`,
                  }}
                >
                  <Play size={24} style={{ color: P.GOLD }} />
                  <div
                    className="absolute top-2 left-2 px-2 py-0.5 rounded text-[9px] font-mono uppercase"
                    style={{
                      background: `${P.NAVY}CC`,
                      color: p.type === "Community Tips" ? P.GOLD : "#A78BFA",
                      letterSpacing: "0.22em",
                    }}
                  >
                    {p.type}
                  </div>
                  <div
                    className="absolute bottom-2 right-2 px-2 py-0.5 rounded text-[9px] font-mono uppercase"
                    style={{
                      background: `${P.NAVY}CC`,
                      color: P.MUTED,
                      letterSpacing: "0.22em",
                    }}
                  >
                    {p.day}
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-sm font-medium leading-snug" style={{ color: P.WHITE }}>
                    {p.title}
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-[10px] font-mono uppercase" style={{ color: P.MUTED, letterSpacing: "0.22em" }}>
                    <span>1.2k views</span>
                    <span>· 34 shares</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Weekly cadence bar */}
          <div className="mt-6 grid grid-cols-7 gap-2" data-testid="jde-growth-week-bar">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => {
              const count = GROWTH_POSTS.filter((p) => p.day === d).length;
              return (
                <div
                  key={d}
                  className="rounded border py-2 px-1 text-center"
                  style={{ borderColor: P.HAIRLINE, background: P.NAVY_DEEP }}
                >
                  <div
                    className="text-[10px] font-mono uppercase"
                    style={{ color: P.GOLD, letterSpacing: "0.24em" }}
                  >
                    {d}
                  </div>
                  <div className="text-lg font-semibold mt-1" style={{ color: count ? P.WHITE : P.MUTED }}>
                    {count}
                  </div>
                  <div className="text-[9px] font-mono uppercase" style={{ color: P.MUTED, letterSpacing: "0.2em" }}>
                    posts
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Pricing Section — Single Dominance Line at $997/mo
// ============================================================
function PricingSection() {
  const tier = DOMINANCE_TIER;
  return (
    <section id="pricing" data-testid="jde-pricing" className="scroll-mt-24">
      <ModuleKicker index="07" title="The Full System" />
      <div className="max-w-3xl mx-auto">
        <div
          data-testid={`jde-tier-${tier.id}`}
          className="rounded-lg border p-8 sm:p-10 relative overflow-hidden"
          style={{
            borderColor: P.GOLD,
            background: `linear-gradient(160deg, ${P.GOLD}22 0%, ${P.NAVY_DEEP} 55%, ${P.NAVY_HI} 100%)`,
            boxShadow: `0 40px 90px -30px ${P.GOLD}66, inset 0 1px 0 ${P.GOLD}44`,
          }}
        >
          {/* Gold shine bar */}
          <div
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background: `linear-gradient(90deg, transparent, ${P.GOLD_HI}, ${P.GOLD}, ${P.GOLD_HI}, transparent)`,
            }}
          />
          <div
            className="text-[10px] font-mono uppercase mb-3"
            style={{ color: P.GOLD, letterSpacing: "0.36em" }}
          >
            {tier.label}
          </div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <div
              className="text-6xl sm:text-7xl font-semibold leading-none"
              style={{ color: P.WHITE }}
              data-testid={`jde-tier-${tier.id}-price`}
            >
              {tier.price}
            </div>
            <div className="text-xl font-mono" style={{ color: P.MUTED }}>
              {tier.period}
            </div>
          </div>
          <div className="mt-3 text-base sm:text-lg" style={{ color: P.WHITE }}>
            {tier.tagline}
          </div>

          <div
            className="my-7 h-px w-full"
            style={{
              background: `linear-gradient(90deg, transparent, ${P.GOLD}66, transparent)`,
            }}
          />

          <ul className="space-y-3.5">
            {tier.features.map((f, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-sm sm:text-base"
                style={{ color: P.WHITE }}
              >
                <Check size={17} style={{ color: P.GOLD }} className="mt-0.5 shrink-0" />
                {f}
              </li>
            ))}
          </ul>

          <button
            type="button"
            data-testid={`jde-tier-${tier.id}-cta`}
            className="mt-9 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded font-semibold text-sm sm:text-base uppercase"
            style={{
              background: P.GOLD,
              color: P.NAVY,
              border: `1px solid ${P.GOLD}`,
              letterSpacing: "0.22em",
              boxShadow: `0 16px 40px -10px ${P.GOLD}`,
            }}
          >
            <Trophy size={16} />
            Lock In Your System
            <ArrowRight size={16} />
          </button>

          <div
            className="mt-5 text-center text-[10px] font-mono uppercase"
            style={{ color: P.MUTED, letterSpacing: "0.32em" }}
          >
            No tiers · No comparisons · One authoritative system
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Footer
// ============================================================
function Footer() {
  return (
    <div
      className="pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-3"
      style={{ borderColor: P.HAIRLINE }}
    >
      <div
        className="text-[10px] font-mono uppercase flex items-center gap-2"
        style={{ color: P.MUTED, letterSpacing: "0.28em" }}
      >
        <MapPin size={11} style={{ color: P.GOLD }} />
        J&D Enterprises LLC · Martinsburg, WV · The Small Business Anchor
      </div>
      <Link
        to="/"
        className="text-[10px] font-mono uppercase"
        style={{ color: P.MUTED, letterSpacing: "0.24em" }}
      >
        <HomeIcon size={11} className="inline mr-1" />
        Return to Grid Lobby
      </Link>
    </div>
  );
}

// ============================================================
// Small helpers
// ============================================================
function ModuleKicker({ index, title }) {
  return (
    <div className="mb-4 flex items-baseline gap-3 flex-wrap">
      <span
        className="text-[10px] font-mono uppercase px-2.5 py-1 rounded"
        style={{
          color: P.NAVY,
          background: P.GOLD,
          letterSpacing: "0.28em",
          fontWeight: 700,
        }}
      >
        Module {index}
      </span>
      <h2 className="text-xl sm:text-2xl font-semibold" style={{ color: P.WHITE }}>
        {title}
      </h2>
    </div>
  );
}

function Pill({ icon: Icon, label }) {
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded"
      style={{
        background: `${P.GOLD}14`,
        border: `1px solid ${P.GOLD}55`,
        color: P.GOLD,
      }}
    >
      <Icon size={12} />
      <span className="text-xs font-mono uppercase" style={{ letterSpacing: "0.2em" }}>
        {label}
      </span>
    </div>
  );
}

function StatCell({ k, v }) {
  return (
    <div>
      <div className="text-2xl font-semibold" style={{ color: P.WHITE }}>{v}</div>
      <div className="text-[9px] font-mono uppercase mt-0.5" style={{ color: P.MUTED, letterSpacing: "0.24em" }}>
        {k}
      </div>
    </div>
  );
}

function BillingChip({ label, active }) {
  return (
    <span
      className="px-3 py-1.5 rounded text-[10px] font-mono uppercase"
      style={{
        background: active ? `${P.GOLD}22` : "transparent",
        color: active ? P.GOLD : P.MUTED,
        border: `1px solid ${active ? P.GOLD : P.HAIRLINE}`,
        letterSpacing: "0.24em",
      }}
    >
      {label}
    </span>
  );
}

function TH({ children, align = "left" }) {
  return (
    <th
      className="p-3 text-[10px] font-mono uppercase"
      style={{
        color: P.GOLD,
        letterSpacing: "0.22em",
        textAlign: align,
        borderBottom: `1px solid ${P.HAIRLINE}`,
        fontWeight: 500,
      }}
    >
      {children}
    </th>
  );
}

function TD({ children, align = "left" }) {
  return (
    <td className="p-3" style={{ textAlign: align, verticalAlign: "middle" }}>
      {children}
    </td>
  );
}

// ============================================================
// TITAN TOUR — guided walkthrough of the 4 flagship modules.
// ------------------------------------------------------------
// A prompt bubble anchored above the Amanda pin appears on load.
// Confirm → dim scrim + gold-glow spotlight on the active module +
// narration card. Prev/Next/Close controls. Uses live
// getBoundingClientRect + scroll sync so the highlight follows the
// element even during smooth-scroll.
// ============================================================
const TOUR_STEPS = [
  {
    id: "intake",
    target: "jde-module-intake",
    title: "Intake Shield",
    body: "Our AI Director answers your phones 24/7, routing Notary, Tax, and Admin calls perfectly.",
  },
  {
    id: "calendar",
    target: "jde-module-calendar",
    title: "Multi-Service Calendar",
    body: "Your new dispatch board keeps every side of your business in its own high-velocity lane.",
  },
  {
    id: "billing",
    target: "jde-module-billing",
    title: "Billing Autopilot",
    body: "Invoicing is now on autopilot. We handle the follow-ups so you can handle the growth.",
  },
  {
    id: "growth",
    target: "jde-module-growth",
    title: "Growth Engine",
    body: "We generate 10 cinematic videos every week to keep J&D the top-of-mind anchor in Martinsburg.",
  },
];

const TOUR_SEEN_KEY = "jde_titan_tour_seen_v1";

function TitanTour() {
  const [phase, setPhase] = useState("idle");   // idle | prompt | running | done
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const rafRef = useRef(null);

  // Show the prompt shortly after mount so the page has painted.
  // Suppress if the operator has already declined/completed the tour
  // in this session.
  useEffect(() => {
    const already = sessionStorage.getItem(TOUR_SEEN_KEY);
    if (already) return;
    const t = setTimeout(() => setPhase("prompt"), 900);
    return () => clearTimeout(t);
  }, []);

  // Sync spotlight rect with the target element on every step,
  // resize, and scroll. rAF keeps it silky during smooth-scroll.
  useLayoutEffect(() => {
    if (phase !== "running") return;
    const targetId = TOUR_STEPS[step]?.target;
    if (!targetId) return;
    const el = document.querySelector(`[data-testid="${targetId}"]`);
    if (!el) return;

    // Bring the target into view first so the highlight is on-screen.
    el.scrollIntoView({ behavior: "smooth", block: "center" });

    const measure = () => {
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      });
      rafRef.current = null;
    };
    const schedule = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    // Keep re-measuring while the smooth-scroll animates.
    const interval = setInterval(schedule, 100);
    const stopInterval = setTimeout(() => clearInterval(interval), 1400);

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      clearInterval(interval);
      clearTimeout(stopInterval);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [phase, step]);

  const startTour = () => {
    setStep(0);
    setPhase("running");
  };

  const declineTour = () => {
    sessionStorage.setItem(TOUR_SEEN_KEY, "declined");
    setPhase("done");
  };

  const closeTour = () => {
    sessionStorage.setItem(TOUR_SEEN_KEY, "closed");
    setPhase("done");
  };

  const next = () => {
    if (step >= TOUR_STEPS.length - 1) {
      sessionStorage.setItem(TOUR_SEEN_KEY, "completed");
      setPhase("done");
    } else {
      setStep((s) => s + 1);
    }
  };

  const prev = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  // Prompt bubble ------------------------------------------------
  if (phase === "prompt") {
    return (
      <div
        className="fixed z-[70] pointer-events-none"
        style={{ right: 100, bottom: 24 }}
        data-testid="titan-tour-prompt-wrapper"
      >
        <div
          className="rounded-lg pointer-events-auto"
          style={{
            background: P.NAVY_DEEP,
            border: `1px solid ${P.GOLD}`,
            boxShadow: `0 30px 60px -20px rgba(0,0,0,0.8), 0 0 32px -8px ${P.GOLD}88`,
            padding: 18,
            maxWidth: 300,
            position: "relative",
          }}
          data-testid="titan-tour-prompt"
        >
          <button
            type="button"
            onClick={declineTour}
            data-testid="titan-tour-prompt-close"
            aria-label="Close"
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              background: "transparent",
              border: "none",
              color: P.MUTED,
              cursor: "pointer",
              padding: 4,
              display: "flex",
            }}
          >
            <XIcon size={14} />
          </button>
          <div
            className="text-[10px] font-mono uppercase mb-2 flex items-center gap-2"
            style={{ color: P.GOLD, letterSpacing: "0.28em" }}
          >
            <Compass size={12} /> Amanda · Live
          </div>
          <div className="text-white text-base font-semibold mb-1">
            Start Virtual Tour?
          </div>
          <div className="text-sm mb-4" style={{ color: P.MUTED }}>
            Two minutes. I&apos;ll walk you through the 4 flagship modules of your J&amp;D system.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={startTour}
              data-testid="titan-tour-start"
              className="flex-1 px-4 py-2.5 rounded font-semibold text-xs uppercase inline-flex items-center justify-center gap-2"
              style={{
                background: P.GOLD,
                color: P.NAVY,
                letterSpacing: "0.2em",
                border: "none",
                cursor: "pointer",
                boxShadow: `0 8px 22px -8px ${P.GOLD}`,
              }}
            >
              <Play size={12} /> Start Tour
            </button>
            <button
              type="button"
              onClick={declineTour}
              data-testid="titan-tour-decline"
              className="px-4 py-2.5 rounded text-xs uppercase font-semibold"
              style={{
                background: "transparent",
                color: P.MUTED,
                border: `1px solid ${P.HAIRLINE}`,
                letterSpacing: "0.2em",
                cursor: "pointer",
              }}
            >
              Later
            </button>
          </div>
          {/* Tail pointing toward the Amanda pin */}
          <div
            style={{
              position: "absolute",
              right: -8,
              bottom: 18,
              width: 14,
              height: 14,
              background: P.NAVY_DEEP,
              borderTop: `1px solid ${P.GOLD}`,
              borderRight: `1px solid ${P.GOLD}`,
              transform: "rotate(45deg)",
            }}
          />
        </div>
      </div>
    );
  }

  // Running overlay ----------------------------------------------
  if (phase === "running" && rect) {
    const current = TOUR_STEPS[step];
    // Padding around the highlight so the glow doesn't hug the border.
    const pad = 8;
    const highlightStyle = {
      position: "fixed",
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
      borderRadius: 12,
      border: `2px solid ${P.GOLD}`,
      boxShadow: `0 0 0 9999px rgba(3, 8, 20, 0.72), 0 0 44px 8px ${P.GOLD}AA, inset 0 0 24px ${P.GOLD}55`,
      pointerEvents: "none",
      transition: "top 320ms ease, left 320ms ease, width 320ms ease, height 320ms ease",
      zIndex: 60,
    };

    // Position the narration card below the highlight when there's
    // room, else above. Fallback to bottom-center on narrow screens.
    const viewportH = typeof window !== "undefined" ? window.innerHeight : 800;
    const spaceBelow = viewportH - (rect.top + rect.height);
    const preferBelow = spaceBelow > 200;
    const cardTop = preferBelow
      ? Math.min(rect.top + rect.height + 24, viewportH - 220)
      : Math.max(24, rect.top - 200);

    return (
      <div
        className="fixed inset-0 z-[65]"
        style={{ pointerEvents: "none" }}
        data-testid="titan-tour-overlay"
      >
        {/* Spotlight (dim + gold ring around target) */}
        <div style={highlightStyle} data-testid="titan-tour-spotlight" />

        {/* Narration card */}
        <div
          className="rounded-lg"
          style={{
            position: "fixed",
            top: cardTop,
            left: "50%",
            transform: "translateX(-50%)",
            width: "min(520px, calc(100% - 32px))",
            background: P.NAVY_DEEP,
            border: `1px solid ${P.GOLD}`,
            boxShadow: `0 30px 80px -20px rgba(0,0,0,0.9), 0 0 40px -8px ${P.GOLD}88`,
            padding: 22,
            pointerEvents: "auto",
            zIndex: 66,
          }}
          data-testid="titan-tour-card"
        >
          <div className="flex items-center justify-between mb-3">
            <div
              className="text-[10px] font-mono uppercase flex items-center gap-2"
              style={{ color: P.GOLD, letterSpacing: "0.32em" }}
              data-testid="titan-tour-step-indicator"
            >
              <Compass size={12} />
              Step {step + 1} of {TOUR_STEPS.length}
            </div>
            <button
              type="button"
              onClick={closeTour}
              data-testid="titan-tour-close"
              aria-label="Close tour"
              style={{
                background: "transparent",
                border: "none",
                color: P.MUTED,
                cursor: "pointer",
                display: "flex",
              }}
            >
              <XIcon size={14} />
            </button>
          </div>
          <div
            className="text-white text-xl sm:text-2xl font-semibold mb-2"
            data-testid="titan-tour-title"
          >
            {current.title}
          </div>
          <div
            className="text-sm sm:text-base leading-relaxed"
            style={{ color: P.MUTED }}
            data-testid="titan-tour-body"
          >
            {current.body}
          </div>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mt-5">
            {TOUR_STEPS.map((s, i) => (
              <div
                key={s.id}
                style={{
                  width: i === step ? 20 : 8,
                  height: 4,
                  borderRadius: 2,
                  background: i <= step ? P.GOLD : `${P.MUTED}44`,
                  transition: "all 250ms ease",
                }}
              />
            ))}
          </div>

          {/* Controls */}
          <div className="mt-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={prev}
              disabled={step === 0}
              data-testid="titan-tour-prev"
              className="px-3 py-2 rounded text-xs uppercase font-semibold inline-flex items-center gap-1.5"
              style={{
                background: "transparent",
                color: step === 0 ? `${P.MUTED}66` : P.MUTED,
                border: `1px solid ${step === 0 ? `${P.HAIRLINE}` : P.HAIRLINE}`,
                letterSpacing: "0.2em",
                cursor: step === 0 ? "not-allowed" : "pointer",
              }}
            >
              <ArrowLeft size={12} /> Prev
            </button>
            <button
              type="button"
              onClick={next}
              data-testid="titan-tour-next"
              className="px-4 py-2.5 rounded font-semibold text-xs uppercase inline-flex items-center gap-2"
              style={{
                background: P.GOLD,
                color: P.NAVY,
                letterSpacing: "0.2em",
                border: "none",
                cursor: "pointer",
                boxShadow: `0 8px 22px -8px ${P.GOLD}`,
              }}
            >
              {step >= TOUR_STEPS.length - 1 ? "Finish Tour" : "Next"}
              <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
